import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { playMessageChime } from '../utils/notificationSound';
import { sanitizeHtml, containsHtml } from '../utils/htmlUtils';

// First message of every chat. Keep the "Hi, I'm Aura" prefix: ChatBot.js uses it to detect a fresh chat.
const WELCOME_TEXT = "Hi, I'm Aura. I can track your cases, send reports, raise reworks and prioritise urgent cases.\nTap a suggestion above, or just ask.";

// Improved HTML detection function that works with partial content
const detectHtmlInStreaming = (text) => {
  if (!text || typeof text !== 'string') return false;
  
  // Common HTML patterns that indicate HTML content
  const htmlPatterns = [
    /<[a-zA-Z][^>]*>/,  // Opening tags
    /<\/[a-zA-Z][^>]*>/,  // Closing tags
    /&[a-zA-Z0-9]+;/,     // HTML entities
    /<[a-zA-Z][^>]*$/,    // Partial opening tag at end
    /style\s*=/i,         // Style attributes
    /class\s*=/i,         // Class attributes
    /<(p|div|span|strong|em|i|b|br|ul|ol|li|h[1-6]|table|tr|td|th)/i  // Common HTML tags
  ];
  
  const hasHtml = htmlPatterns.some(pattern => pattern.test(text));
  
  // Also check for common HTML content indicators
  const hasHtmlContent = text.includes('<p>') || text.includes('<div>') || 
                        text.includes('<span>') || text.includes('<strong>') ||
                        text.includes('<em>') || text.includes('<b>') ||
                        text.includes('<i>') || text.includes('<br>') ||
                        text.includes('<ul>') || text.includes('<ol>') ||
                        text.includes('<li>') || text.includes('<h1>') ||
                        text.includes('<h2>') || text.includes('<h3>') ||
                        text.includes('<table>') || text.includes('<tr>') ||
                        text.includes('<td>') || text.includes('<th>');
  
  return hasHtml || hasHtmlContent;
};

// Buffer to handle partial HTML tags
let htmlBuffer = '';
let isHtmlDetected = false;

// Function to process streaming HTML content
const processStreamingHtml = (newChunk, existingContent = '') => {
  const fullContent = existingContent + newChunk;
  
  // Early HTML detection - check if we haven't detected HTML yet
  if (!isHtmlDetected) {
    isHtmlDetected = detectHtmlInStreaming(fullContent);
    // console.log('🔍 HTML Detection in processStreamingHtml:', {
    //   newChunk: newChunk.substring(0, 50),
    //   fullContent: fullContent.substring(0, 100),
    //   isHtmlDetected,
    //   detectionResult: detectHtmlInStreaming(fullContent)
    // });
  }
  
  // If we detect HTML, we want to render it immediately
  // even if tags are incomplete
  if (isHtmlDetected) {
    // Handle partial tags at the end of content
    const lastOpenBracket = fullContent.lastIndexOf('<');
    const lastCloseBracket = fullContent.lastIndexOf('>');
    
    // If there's an unclosed tag at the end, we might have a partial tag
    if (lastOpenBracket > lastCloseBracket) {
      // Check if it looks like a partial tag (not just a less-than symbol)
      const potentialTag = fullContent.substring(lastOpenBracket);
      if (potentialTag.match(/^<[a-zA-Z][^>]*$/)) {
        // This looks like a partial tag, but still render the content
        // The browser will handle partial tags gracefully
        // console.log('⚠️ Partial tag detected during streaming:', potentialTag);
        return {
          content: fullContent,
          isHtml: true,
          hasPartialTag: true
        };
      }
    }
  }
  
  const result = {
    content: fullContent,
    isHtml: isHtmlDetected,
    hasPartialTag: false
  };
  
  // console.log('📊 processStreamingHtml result:', {
  //   contentLength: fullContent.length,
  //   isHtml: result.isHtml,
  //   preview: fullContent.substring(0, 100)
  // });
  
  return result;
};

// USER / BOT / AGENT (a live 5C support agent) / SYSTEM (status line)
const senderForType = (type) => ({ USER: 'user', AGENT: 'agent', SYSTEM: 'system' }[type] || 'bot');

// This browser's live chat, so reopening Aura returns to it. Kept per browser on purpose:
// people sharing one login must not land in (or end) each other's chats.
const liveChatKey = (clientData) => `aura_live_chat_${clientData?.clientId}_${clientData?.email}`;
const rememberLiveChat = (clientData, sessionId) => {
  try { localStorage.setItem(liveChatKey(clientData), sessionId); } catch (e) { /* storage blocked */ }
};
const forgetLiveChat = (clientData) => {
  try { localStorage.removeItem(liveChatKey(clientData)); } catch (e) { /* storage blocked */ }
};
const rememberedLiveChat = (clientData) => {
  try { return localStorage.getItem(liveChatKey(clientData)); } catch (e) { return null; }
};

// While a live chat with a 5C agent is open the widget polls this often (ms)
export const AGENT_POLL_INTERVAL_MS = 3000;

// Agent replies and status lines since the last one shown; also tells us whether a live chat is open.
// Never rejects: a failed poll just waits for the next one instead of showing a network error.
export const fetchAgentUpdates = createAsyncThunk(
  'chat/fetchAgentUpdates',
  async (_, { getState, dispatch }) => {
    const { sessionId, handoff, clientData } = getState().chat;
    // A burst is still being shown; the next poll picks up anything newer
    if (!sessionId || revealing) return null;
    try {
      const {REACT_APP_BACKEND_BOT_URL,REACT_APP_AUTH_TOKEN} = process.env;
      const response = await fetch(
        `${REACT_APP_BACKEND_BOT_URL}/chat/updates/${sessionId}?after_id=${handoff.lastId || 0}`,
        { headers: { 'Content-Type': 'application/json', 'Authorization': `${REACT_APP_AUTH_TOKEN}` } }
      );
      if (!response.ok) return null;
      const update = await response.json();
      if (update.poll && !update.locked && update.mode !== 'BOT') {
        rememberLiveChat(clientData, sessionId);
      } else if (rememberedLiveChat(clientData) === sessionId) {
        forgetLiveChat(clientData);
      }
      dispatch(handoffStateUpdated(update));
      await revealAgentMessages(update, dispatch);
      return update;
    } catch (error) {
      return null;
    }
  }
);

// Messages that land together (Aura's reply, "Connected to ...", the agent's welcome) are shown
// one after another, with the agent "typing" first, so the burst doesn't look like a glitch.
// A message on its own (a normal agent reply) still appears at once.
const BURST_WINDOW_MS = 1500;
const DIVIDER_GAP_MS = 700;
const AGENT_TYPING_MS = 1300;
let lastShownAt = 0;
let revealing = false;
const pause = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
export const noteMessageShown = () => { lastShownAt = Date.now(); };

const revealAgentMessages = async (update, dispatch) => {
  const messages = update.messages || [];
  revealing = true;
  try {
    for (const msg of messages) {
      if (Date.now() - lastShownAt < BURST_WINDOW_MS) {
        if (msg.type === 'AGENT') {
          dispatch(setAgentTyping(msg.sender_name || '5C Support'));
          await pause(AGENT_TYPING_MS);
          dispatch(setAgentTyping(null));
        } else {
          await pause(DIVIDER_GAP_MS);
        }
      }
      dispatch(agentMessageRevealed({ sessionId: update.session_id, msg }));
      // A new message from the agent (polling only brings new ones; history never chimes)
      if (msg.type === 'AGENT') playMessageChime();
      noteMessageShown();
    }
  } finally {
    revealing = false;
  }
};

// Upload one file whose tile is already showing (attachmentSending). The backend passes it to
// the CRM ticket and returns the stored file. Never throws: a problem shows on the tile.
const uploadAttachment = async (sessionId, localId, file, dispatch) => {
  try {
    const {REACT_APP_BACKEND_BOT_URL,REACT_APP_AUTH_TOKEN} = process.env;
    const formData = new FormData();
    formData.append('file', file, file.name);
    const response = await fetch(`${REACT_APP_BACKEND_BOT_URL}/chat/attachment/${sessionId}`, {
      method: 'POST',
      headers: { 'Authorization': `${REACT_APP_AUTH_TOKEN}` },  // the browser sets the multipart type
      body: formData,
    });
    if (response.ok) {
      dispatch(attachmentSent({ localId, result: await response.json() }));
      noteMessageShown();
      return true;
    }
    let detail = '';
    try { detail = (await response.json())?.detail || ''; } catch (e) { /* not JSON */ }
    if (response.status === 409) {
      dispatch(attachmentFailed({ localId, error: 'Not sent: this chat has ended' }));
      return 'ended';
    }
    dispatch(attachmentFailed({ localId, error: response.status === 422 && detail ? detail : 'Not sent. Please try again.' }));
  } catch (error) {
    dispatch(attachmentFailed({ localId, error: 'Not sent. Check your connection and try again.' }));
  }
  return false;
};

// Send files to the agent during a live chat, in the order chosen. Each is its own message,
// as in Nexus. All the tiles show at once and fill in as each file is stored. Files were
// checked when chosen (attachmentProblem); the backend checks again.
export const sendAttachments = createAsyncThunk(
  'chat/sendAttachments',
  async ({ files }, { getState, dispatch }) => {
    const { sessionId } = getState().chat;
    if (!sessionId || !files.length) return null;
    const batch = `${Date.now()}`;
    const localIds = files.map((file, index) => `file-${batch}-${index}`);
    files.forEach((file, index) => dispatch(attachmentSending({ localId: localIds[index], file })));
    for (let index = 0; index < files.length; index += 1) {
      // eslint-disable-next-line no-await-in-loop
      const outcome = await uploadAttachment(sessionId, localIds[index], files[index], dispatch);
      if (outcome === 'ended') {
        // The chat ended meanwhile: the rest aren't sent; the next poll shows why and locks the chat
        localIds.slice(index + 1).forEach(localId =>
          dispatch(attachmentFailed({ localId, error: 'Not sent: this chat has ended' })));
        dispatch(fetchAgentUpdates());
        break;
      }
    }
    return null;
  }
);

// Fetch chat history for a session
export const fetchChatHistory = createAsyncThunk(
  'chat/fetchChatHistory',
  async (sessionId, { getState, rejectWithValue }) => {
    try {
      const {REACT_APP_BACKEND_BOT_URL,REACT_APP_AUTH_TOKEN} = process.env;
      const response = await fetch(`${REACT_APP_BACKEND_BOT_URL}/chat/history/${sessionId}`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json', 'Authorization': `${REACT_APP_AUTH_TOKEN}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        return data?.history?.map(item => ({
          id: item.id,
          text: item.text,
          isUser: item.type=='USER' ? true : false,
          sender: senderForType(item.type),
          senderName: item.sender_name || null,
          timestamp: item.created_at,
          attachment: item.attachment || null,
          isHtml: true
        })); // Assuming the API returns an array of messages
      } else {
        throw new Error('Failed to fetch chat history');
      }
    } catch (error) {
      console.error('Error fetching chat history:', error);
      return []; // Return empty array if history fetch fails
    }
  }
);

// Initialize new session without fetching history (for retry scenarios)
export const createRetrySession = createAsyncThunk(
  'chat/createRetrySession',
  async (_, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      const {REACT_APP_BACKEND_BOT_URL,REACT_APP_AUTH_TOKEN} = process.env;
      const response = await fetch(`${REACT_APP_BACKEND_BOT_URL}/chat/new-session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `${REACT_APP_AUTH_TOKEN}` },
        body: JSON.stringify({
          client_id: state.chat.clientData?.clientId?.toString() ,
          user_email: state.chat.clientData?.email?.toString() 
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        const sessionId = data.session_id;
        
        // Don't fetch chat history - preserve current conversation
        return sessionId;
      } else {
        return rejectWithValue('Failed to create retry session');
      }
    } catch (error) {
      console.error('Error creating retry session:', error);
      return rejectWithValue('Retry session creation failed');
    }
  }
);

// Initialize new session and fetch history
export const initializeSession = createAsyncThunk(
  'chat/initializeSession',
  async (_, { getState, dispatch, rejectWithValue }) => {
    try {
      const state = getState();
      const {REACT_APP_BACKEND_BOT_URL,REACT_APP_AUTH_TOKEN} = process.env;
      const response = await fetch(`${REACT_APP_BACKEND_BOT_URL}/chat/new-session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `${REACT_APP_AUTH_TOKEN}` },
        body: JSON.stringify({
          client_id: state.chat.clientData?.clientId?.toString()  ,
          user_email: state.chat.clientData?.email?.toString(),
          resume_session_id: rememberedLiveChat(state.chat.clientData) || undefined
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        const sessionId = data.session_id;
        
        // Fetch chat history for this session
        await dispatch(fetchChatHistory(sessionId));
        
        return sessionId;
      } else {
        const errorText = await response.text();
        return rejectWithValue(`Failed to initialize session: ${response.status} ${errorText}`);
      }
    } catch (error) {
      console.error('Error initializing session:', error);
      return rejectWithValue(`Connection failed: ${error.message}`);
    }
  }
);

// Stream bot response from API
export const streamBotResponse = createAsyncThunk(
  'chat/streamBotResponse',
  async (request, { dispatch, getState, rejectWithValue }) => {
    // Accepts the message text, or { text, source } where source is 'typed', 'chip' or 'next_step'
    const { text: userMessage, source = 'typed' } = typeof request === 'string' ? { text: request } : request;
    const state = getState();
    let sessionId = state.chat.sessionId;
    const clientData = state.chat.clientData;
    
    if (!sessionId) {
      throw new Error('No session ID available');
    }

    const botMessageId = Date.now() + 1;
    
    // Find the current user message (last user message in the array)
    const currentUserMessage = [...state.chat.messages].reverse().find(msg => msg.isUser === true);
    const currentUserMessageId = currentUserMessage?.id;
    
    // Reset HTML detection for new message
    isHtmlDetected = false;
    htmlBuffer = '';
    
    // The bot's reply bubble ("Thinking...") appears at once. In a live chat the message goes
    // to the agent and no bot reply comes, so the bubble appears only if text does come back
    // (an error, or "this chat has ended").
    const { handoff } = state.chat;
    const inLiveChat = handoff.mode !== 'BOT' && !handoff.locked;
    let botBubbleShown = false;
    const showBotBubble = () => {
      if (botBubbleShown) return;
      botBubbleShown = true;
      dispatch(addMessage({
        id: botMessageId,
        text: '',
        isUser: false,
        timestamp: new Date().toISOString(),
        isLoading: !inLiveChat,
        isStreaming: true,
        isHtml: false
      }));
    };
    if (!inLiveChat) showBotBubble();

    const makeStreamRequest = async (currentSessionId, retryCount = 0) => {
      // console.log(`Making stream request with session: ${currentSessionId}, retry count: ${retryCount}`);
      // console.log(`Question being asked: "${userMessage}"`);
      
      try {
        const {REACT_APP_BACKEND_BOT_URL,REACT_APP_AUTH_TOKEN} = process.env;
        const response = await fetch(`${REACT_APP_BACKEND_BOT_URL}/chat/stream`, {
          method: 'POST',
          headers: { 
            'Content-Type': 'application/json',
            'Accept': 'text/event-stream',
            'Cache-Control': 'no-cache',
            'Authorization': `${REACT_APP_AUTH_TOKEN}`
          },
          body: JSON.stringify({
            client_id: clientData?.clientId,
            user_email: clientData?.email,
            message: userMessage,
            session_id: currentSessionId,
            source
          })
        });

        // Check if response indicates an error before processing stream
        if (!response.ok) {
          const errorText = await response.text();
          let errorData;
          
          try {
            errorData = JSON.parse(errorText);
          } catch (parseError) {
            throw new Error(`HTTP ${response.status}: ${errorText}`);
          }
          
          // A live chat that ended (no agent in time, agent closed it) takes no more messages
          if (errorData.detail === "chat ended") {
            dispatch(removeMessage(botMessageId));
            dispatch(setChatLocked(true));
            return { id: botMessageId, content: '', isHtml: false };
          }

          // Check for session expired error
          if (errorData.detail === "session expired" && retryCount === 0) {
            // console.log('🔄 Session expired, clearing history except current question...');
            
            // Clear all messages except current user question and bot response
            dispatch(clearMessagesExceptCurrent({
              currentUserMessageId: currentUserMessageId,
              currentBotMessageId: botMessageId
            }));
            
            // Update the bot message to show retry status
            showBotBubble(); dispatch(updateStreamingMessage({
              id: botMessageId,
              content: 'Session expired, creating a new session...',
              isStreaming: true,
              isHtml: false
            }));
            
            // Create new session WITHOUT fetching history
            const newSessionResult = await dispatch(createRetrySession());
            
            if (createRetrySession.fulfilled.match(newSessionResult)) {
              const newSessionId = newSessionResult.payload;
              // console.log(`✅ New retry session created: ${newSessionId}`);
              // console.log(`🔄 Automatically retrying question: "${userMessage}"`);
              
              // Update message to show we're processing with new session
              showBotBubble(); dispatch(updateStreamingMessage({
                id: botMessageId,
                content: 'New session created, processing your question...',
                isStreaming: true,
                isHtml: false
              }));
              
              // Small delay before retry
              await new Promise(resolve => setTimeout(resolve, 800));
              
              // Clear content and start fresh streaming with new session
              showBotBubble(); dispatch(updateStreamingMessage({
                id: botMessageId,
                content: '',
                isStreaming: true,
                isHtml: false
              }));
              
              // Retry the SAME question with the NEW session ID
              return await makeStreamRequest(newSessionId, retryCount + 1);
            } else {
              throw new Error('Failed to create retry session after expiration');
            }
          } else {
            throw new Error(errorData.detail || `HTTP ${response.status}: ${errorText}`);
          }
        }

        if (!response.body) {
          throw new Error('No response body');
        }
        
        const reader = response.body.getReader();
        const decoder = new TextDecoder('utf-8');
        let buffer = '';
        let fullText = '';
        
        while (true) {
          const { value, done } = await reader.read();
          if (done) break;
          
          if (value) {
            buffer += decoder.decode(value, { stream: true });
            const lines = buffer.split('\n');
            buffer = lines.pop() || '';
            
            for (const line of lines) {
              if (line.startsWith('data: ')) {
                try {
                  const jsonData = line.substring(6);
                  const parsed = JSON.parse(jsonData);
                  
                  if (parsed.type === 'chunk') {
                    // Process the new chunk with existing content
                    const processedContent = processStreamingHtml(parsed.content, fullText);
                    fullText = processedContent.content;
                    
                    // Update the SAME message with new content
                    showBotBubble(); dispatch(updateStreamingMessage({
                      id: botMessageId,
                      content: fullText,
                      isHtml: processedContent.isHtml,
                      isStreaming: true
                    }));
                    
                    // Add a small delay to make streaming more visible
                    await new Promise(resolve => setTimeout(resolve, 10));
                  } else if (parsed.type === 'forwarded') {
                    // Sent to the live agent; drop the empty bot bubble, their reply arrives via polling
                    dispatch(removeMessage(botMessageId));
                    dispatch(markSentToAgent(currentUserMessageId));
                  } else if (parsed.type === 'contact_picker') {
                    // Aura asked for a name and number: offer the client's saved contacts under this reply
                    dispatch(setMessageContactPicker(botMessageId));
                  } else if (parsed.type === 'suggestions') {
                    // Next-step buttons for this reply, chosen by the backend from the tool that answered
                    try {
                      const items = JSON.parse(parsed.content);
                      if (Array.isArray(items)) {
                        dispatch(setMessageSuggestions({ id: botMessageId, suggestions: items.filter((t) => typeof t === 'string') }));
                      }
                    } catch (e) {
                      // ignore a malformed list; the reply itself is unaffected
                    }
                  } else if (parsed.type === 'done') {
                    // Final processing
                    const finalProcessed = processStreamingHtml('', fullText);
                    dispatch(completeStreamingMessage({
                      id: botMessageId,
                      content: fullText,
                      isHtml: finalProcessed.isHtml
                    }));
                    break;
                  } else if (parsed.type === 'error') {
                    // Handle streaming errors that might include session expiration
                    if (parsed.detail === "session expired" && retryCount === 0) {
                      // console.log('🔄 Session expired during streaming, clearing history...');
                      
                      // Clear all messages except current user question and bot response
                      dispatch(clearMessagesExceptCurrent({
                        currentUserMessageId: currentUserMessageId,
                        currentBotMessageId: botMessageId
                      }));
                      
                      // Update the existing bot message
                      showBotBubble(); dispatch(updateStreamingMessage({
                        id: botMessageId,
                        content: 'Session expired, creating a new session...',
                        isStreaming: true,
                        isHtml: false
                      }));
                      
                      // Create new session WITHOUT fetching history
                      const newSessionResult = await dispatch(createRetrySession());
                      
                      if (createRetrySession.fulfilled.match(newSessionResult)) {
                        const newSessionId = newSessionResult.payload;
                        // console.log(`✅ New retry session created: ${newSessionId}`);
                        
                        // Update message and reset for new attempt
                        showBotBubble(); dispatch(updateStreamingMessage({
                          id: botMessageId,
                          content: 'Retrying...',
                          isStreaming: true,
                          isHtml: false
                        }));
                        
                        await new Promise(resolve => setTimeout(resolve, 500));
                        
                        // Clear and start fresh
                        showBotBubble(); dispatch(updateStreamingMessage({
                          id: botMessageId,
                          content: '',
                          isStreaming: true,
                          isHtml: false
                        }));
                        
                        // Retry the SAME question with the NEW session ID
                        return await makeStreamRequest(newSessionId, retryCount + 1);
                      } else {
                        throw new Error('Failed to create retry session');
                      }
                    } else {
                      throw new Error(parsed.detail || 'Streaming error occurred');
                    }
                  }
                } catch (parseError) {
                  console.warn('Failed to parse SSE data:', line, parseError);
                }
              }
            }
          }
        }
        
        // Final update with complete content
        const finalProcessed = processStreamingHtml('', fullText);
        
        dispatch(completeStreamingMessage({
          id: botMessageId,
          content: fullText,
          isHtml: finalProcessed.isHtml
        }));
        
        return { 
          id: botMessageId, 
          content: fullText, 
          isHtml: finalProcessed.isHtml 
        };
        
      } catch (error) {
        console.error('Error in stream request:', error);
        throw error;
      }
    };

    try {
      const result = await makeStreamRequest(sessionId);
      noteMessageShown();
      // The reply may have opened a live chat with an agent; this starts polling if so
      dispatch(fetchAgentUpdates());
      return result;
    } catch (error) {
      console.error('Error streaming message:', error);
      
      // Update the existing message with error
      showBotBubble(); dispatch(updateStreamingMessage({
        id: botMessageId,
        content: 'Sorry, I encountered an error. Please try again.',
        isError: true,
        isStreaming: false
      }));
      
      dispatch(completeStreamingMessage({
        id: botMessageId,
        content: 'Sorry, I encountered an error. Please try again.'
      }));
      
      throw error;
    }
  }
);

// Process message queue
export const processMessageQueue = createAsyncThunk(
  'chat/processMessageQueue',
  async (_, { getState, dispatch, rejectWithValue }) => {
    let state = getState();
    const { messageQueue, isStreaming, isProcessingQueue } = state.chat;
    
    // Don't process if already processing, streaming, or queue is empty
    if (isProcessingQueue || isStreaming || messageQueue.length === 0) {
      console.log('Queue processing skipped:', { isProcessingQueue, isStreaming, queueLength: messageQueue.length });
      return { processed: 0, reason: 'Not ready to process queue' };
    }
    
    console.log('Starting queue processing with', messageQueue.length, 'messages');
    dispatch(setProcessingQueue(true));
    
    try {
      let processedCount = 0;
      
      // Process messages one by one
      while (true) {
        // Get fresh state
        state = getState();
        const currentQueue = state.chat.messageQueue;
        
        // Check if queue is empty or streaming started
        if (currentQueue.length === 0 || state.chat.isStreaming) {
          console.log('Stopping queue processing:', { queueEmpty: currentQueue.length === 0, isStreaming: state.chat.isStreaming });
          break;
        }
        
        // Get the next message from queue
        const queueItem = currentQueue[0];
        if (!queueItem) break;
        
        console.log('Processing queued message:', queueItem.message.substring(0, 50) + '...');
        
        // Remove from queue first
        dispatch(dequeueMessage());
        
        // Add user message to chat
        dispatch(sendUserMessage(queueItem.message));
        
        // Send the message and wait for completion
        try {
          await dispatch(streamBotResponse({ text: queueItem.message, source: queueItem.source })).unwrap();
          processedCount++;
          console.log('Successfully processed queued message', processedCount);
        } catch (error) {
          console.error('Error processing queued message:', error);
          // Continue processing other messages even if one fails
        }
        
        // Small delay before next message
        await new Promise(resolve => setTimeout(resolve, 500));
      }
      
      console.log('Queue processing completed. Processed', processedCount, 'messages');
      return { processed: processedCount };
    } catch (error) {
      console.error('Error processing message queue:', error);
      return rejectWithValue(error.message);
    } finally {
      dispatch(setProcessingQueue(false));
    }
  }
);

// Close session with feedback
export const closeSession = createAsyncThunk(
  'chat/closeSession',
  async ({ sessionId, feedback = '', shouldCreateNew = false }, { getState, dispatch, rejectWithValue }) => {
    try {
      const {REACT_APP_BACKEND_BOT_URL,REACT_APP_AUTH_TOKEN} = process.env;
      // First, close the current session
      const response = await fetch(`${REACT_APP_BACKEND_BOT_URL}/chat/session/close/${sessionId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `${REACT_APP_AUTH_TOKEN}` },
        body: JSON.stringify({
          feedback: feedback
        })  
      });
      
      if (response.ok) {
        console.log('Session closed successfully');
        
        // A chat the user left on purpose is not resumed
        if (rememberedLiveChat(getState().chat.clientData) === sessionId) {
          forgetLiveChat(getState().chat.clientData);
        }

        // Only create new session if shouldCreateNew is true AND session close was successful
        if (shouldCreateNew) {
          console.log('Creating new session after successful close...');
          // Wait for the new session to be created
          const newSessionResult = await dispatch(initializeSession());
          
          // Check if new session creation was successful
          if (initializeSession.fulfilled.match(newSessionResult)) {
            console.log('New session created successfully:', newSessionResult.payload);
            return { 
              sessionId, 
              shouldCreateNew, 
              newSessionId: newSessionResult.payload,
              success: true 
            };
          } else {
            console.error('Failed to create new session');
            throw new Error('Failed to create new session after closing');
          }
        }
        
        return { 
          sessionId, 
          shouldCreateNew: false, 
          success: true 
        };
      } else {
        throw new Error('Failed to close session');
      }
    } catch (error) {
      console.error('Error closing session:', error);
      throw error;
    }
  }
);

// Add error state to your initial state
const initialState = {
  messages: [], // Start with empty messages, will be populated by history or welcome message
  inputText: '',
  isTyping: false,
  loading: false,
  sessionId: null,
  isInitializingSession: false,
  isStreaming: false,
  isLoadingHistory: true, // Add this new loading state for history
  isClosingSession: false, // Add loading state for closing session
  // Add error handling states
  networkError: null,
  sessionError: null, // Add specific session error state
  retryCount: 0,
  isOffline: false,
  clientData: {
    clientId: null,
    email: null
  },
  // Message queue functionality
  messageQueue: [], // Queue for pending messages to be sent
  isProcessingQueue: false, // Flag to track if queue is being processed
  // Live chat with a 5C agent: mode BOT / WAITING_AGENT / AGENT, poll while one is open
  handoff: { mode: 'BOT', agentName: null, poll: false, lastId: 0, locked: false, checked: false, csat: false, typingAgent: null }
};

// Add error handling reducers
const chatSlice = createSlice({
  name: 'chat',
  initialState,
  reducers: {
    addMessage: (state, action) => {
      state.messages.push(action.payload);
    },
    removeMessage: (state, action) => {
      state.messages = state.messages.filter(msg => msg.id !== action.payload);
    },
    markSentToAgent: (state, action) => {
      const message = state.messages.find(msg => msg.id === action.payload);
      if (message) message.sentToAgent = true;
    },
    handoffStateUpdated: (state, action) => {
      const update = action.payload;
      // Ignore a poll that finished after the session changed
      if (!update || update.session_id !== state.sessionId) return;
      state.handoff.mode = update.mode || 'BOT';
      state.handoff.agentName = update.agent_name || null;
      state.handoff.poll = Boolean(update.poll);
      state.handoff.locked = Boolean(update.locked);
      state.handoff.csat = Boolean(update.csat);
      // Advance now, so a poll during the reveal doesn't fetch these again
      (update.messages || []).forEach(msg => { state.handoff.lastId = Math.max(state.handoff.lastId, msg.id); });
    },
    agentMessageRevealed: (state, action) => {
      const { sessionId, msg } = action.payload;
      if (sessionId !== state.sessionId || state.messages.some(m => m.id === msg.id)) return;
      state.messages.push({
        id: msg.id,
        text: msg.text,
        isUser: false,
        sender: senderForType(msg.type),
        senderName: msg.sender_name || null,
        timestamp: msg.created_at || new Date().toISOString(),
        attachment: msg.attachment || null,
        isLoading: false,
        isStreaming: false,
        isHtml: true,
        fresh: true  // fades in; history and resumed chats don't
      });
    },
    // The client's file is on its way to the agent
    attachmentSending: (state, action) => {
      const { localId, file } = action.payload;
      state.messages.push({
        id: localId,
        text: '',
        isUser: true,
        sender: 'user',
        timestamp: new Date().toISOString(),
        attachment: { file_name: file.name, file_size: file.size },  // gets its id once stored
        uploading: true,
        isLoading: false,
        isStreaming: false,
        isHtml: false
      });
    },
    attachmentSent: (state, action) => {
      const { localId, result } = action.payload;
      const message = state.messages.find(msg => msg.id === localId);
      if (!message) return;
      message.id = result.message_id || localId;
      message.attachment = result.attachment;
      message.uploading = false;
      message.sentToAgent = true;
    },
    attachmentFailed: (state, action) => {
      const { localId, error } = action.payload;
      const message = state.messages.find(msg => msg.id === localId);
      if (!message) return;
      message.uploading = false;
      message.uploadError = error;
    },
    setAgentTyping: (state, action) => {
      state.handoff.typingAgent = action.payload;
    },
    setChatLocked: (state, action) => {
      state.handoff.locked = Boolean(action.payload);
    },
    setInputText: (state, action) => {
      state.inputText = action.payload;
    },
    clearInput: (state) => {
      state.inputText = '';
    },
    setIsTyping: (state, action) => {
      state.isTyping = action.payload;
    },
    setIsStreaming: (state, action) => {
      state.isStreaming = action.payload;
    },
    sendUserMessage: (state, action) => {
      const userMessage = {
        id: Date.now(),
        text: action.payload,
        isUser: true,
        timestamp: new Date().toISOString(),
        isLoading: false,
        isStreaming: false,
        isHtml: false
      };
      state.messages.push(userMessage);
      state.inputText = '';
    },
    updateStreamingMessage: (state, action) => {
      const { id, content, isError, isHtml, isStreaming } = action.payload;
      const messageIndex = state.messages.findIndex(msg => msg.id === id);
      if (messageIndex !== -1) {
        // console.log('updateStreamingMessage - updating message:', {
        //   id,
        //   contentLength: content?.length || 0,
        //   isHtml,
        //   isError,
        //   isStreaming
        // });
        state.messages[messageIndex].text = content;
        state.messages[messageIndex].isLoading = false;
        state.messages[messageIndex].isHtml = isHtml || false;
        state.messages[messageIndex].isStreaming = isStreaming !== undefined ? isStreaming : true;
        if (isError) {
          state.messages[messageIndex].isError = true;
          state.messages[messageIndex].isStreaming = false;
        }
      }
    },
    completeStreamingMessage: (state, action) => {
      const { id, content, isHtml } = action.payload;
      const messageIndex = state.messages.findIndex(msg => msg.id === id);
        if (messageIndex !== -1) {
          // console.log('completeStreamingMessage - completing message:', {
          //   id,
          //   contentLength: content?.length || 0,
          //   isHtml
          // });
        state.messages[messageIndex].text = content;
        state.messages[messageIndex].isLoading = false;
        state.messages[messageIndex].isStreaming = false;
        state.messages[messageIndex].isHtml = isHtml || false;
      }
      state.isStreaming = false;
    },
    setMessageContactPicker: (state, action) => {
      const message = state.messages.find((msg) => msg.id === action.payload);
      if (message) message.contactPicker = true;
    },
    setMessageSuggestions: (state, action) => {
      const { id, suggestions } = action.payload;
      const message = state.messages.find((msg) => msg.id === id);
      if (message) {
        message.suggestions = suggestions;
      }
    },
    setClientData: (state, action) => {
      const { clientId, email } = action.payload;
      // console.log('clientId', clientId);
      // console.log('email', email);
      state.clientData.clientId = clientId;
      state.clientData.email = email;
    },
    clearClientData: (state) => {
      state.clientData.clientId = null;
      state.clientData.email = null;
    },
    setChatHistory: (state, action) => {
      // Replace messages with history, but keep welcome message if no history
      const historyMessages = action.payload;
      if (historyMessages && historyMessages.length > 0) {
        state.messages = historyMessages.map(msg => ({
          id: msg.id || Date.now() + Math.random(),
          text: msg.text || msg.message || msg.content,
          isUser: msg.isUser || msg.is_user || false,
          timestamp: msg.timestamp || msg.created_at || new Date().toISOString(),
          isLoading: false,
          isStreaming: false,
          isHtml: msg.isHtml || msg.is_html || false
        }));
      } else {
        // No history, show welcome message
        state.messages = [{
          id: 1,
          text: WELCOME_TEXT,
          isWelcome: true, // drawn as the WelcomeHero, not as a chat bubble
          isUser: false,
          timestamp: new Date().toISOString(),
          isLoading: false,
          isStreaming: false,
          isHtml: false
        }];
      }
    },
    clearSession: (state) => {
      state.sessionId = null;
      state.messages = [];
      state.handoff = initialState.handoff;
    },
    resetToWelcomeMessage: (state) => {
      state.messages = [{
        id: 1,
        text: WELCOME_TEXT,
        isWelcome: true, // drawn as the WelcomeHero, not as a chat bubble
        isUser: false,
        timestamp: new Date().toISOString(),
        isLoading: false,
        isStreaming: false,
        isHtml: false
      }];
    },
    // Add a new reducer to clear messages except current question
    clearMessagesExceptCurrent: (state, action) => {
      const { currentUserMessageId, currentBotMessageId } = action.payload;
      
      // Keep only the current user question and bot response
      state.messages = state.messages.filter(msg => 
        msg.id === currentUserMessageId || msg.id === currentBotMessageId
      );
    },
    setNetworkError: (state, action) => {
      state.networkError = action.payload;
    },
    clearNetworkError: (state) => {
      state.networkError = null;
      state.retryCount = 0;
    },
    setSessionError: (state, action) => {
      state.sessionError = action.payload;
    },
    clearSessionError: (state) => {
      state.sessionError = null;
      state.retryCount = 0;
    },
    incrementRetryCount: (state) => {
      state.retryCount += 1;
    },
    setOfflineStatus: (state, action) => {
      state.isOffline = action.payload;
    },
    // Message queue management
    enqueueMessage: (state, action) => {
      const { message, priority = 'normal', source = 'typed' } = action.payload;
      const queueItem = {
        id: Date.now() + Math.random(),
        message,
        priority,
        source,
        timestamp: new Date().toISOString()
      };
      
      // Add to queue based on priority (high priority items go to front)
      if (priority === 'high') {
        state.messageQueue.unshift(queueItem);
      } else {
        state.messageQueue.push(queueItem);
      }
    },
    dequeueMessage: (state) => {
      state.messageQueue.shift();
    },
    clearMessageQueue: (state) => {
      state.messageQueue = [];
    },
    setProcessingQueue: (state, action) => {
      state.isProcessingQueue = action.payload;
    }
  },
  extraReducers: (builder) => {
    builder
      // Session initialization
      .addCase(initializeSession.pending, (state) => {
        state.isInitializingSession = true;
        state.sessionError = null; // Clear previous errors
      })
      .addCase(initializeSession.fulfilled, (state, action) => {
        state.isInitializingSession = false;
        state.sessionId = action.payload;
        state.sessionError = null; // Clear any previous errors
        // The first agent-updates poll fills this in (a resumed live chat comes back as open)
        state.handoff = { ...state.handoff, mode: 'BOT', agentName: null, poll: false, locked: false, checked: false, csat: false };
      })
      .addCase(initializeSession.rejected, (state, action) => {
        state.isInitializingSession = false;
        state.sessionError = action.payload || 'Failed to initialize session';
        state.sessionId = null; // Don't create fallback session
      })
      // Retry session creation (doesn't fetch history)
      .addCase(createRetrySession.pending, (state) => {
        // Don't set isInitializingSession to avoid UI disruption
      })
      .addCase(createRetrySession.fulfilled, (state, action) => {
        state.sessionId = action.payload;
        // console.log('Retry session set in store:', action.payload);
      })
      .addCase(createRetrySession.rejected, (state) => {
        // console.error('Retry session creation failed');
      })
      // Chat history fetching
      .addCase(fetchChatHistory.pending, (state) => {
        state.isLoadingHistory = true; // Set history loading to true
      })
      .addCase(fetchChatHistory.fulfilled, (state, action) => {
        state.isLoadingHistory = false; // Set history loading to false
        // Set the chat history
        const historyMessages = action.payload;
        state.handoff.lastId = 0;
        if (historyMessages && historyMessages.length > 0) {
          state.messages = historyMessages.map(msg => ({
            id: msg.id || Date.now() + Math.random(),
            text: msg.text || msg.message || msg.content,
            isUser: msg.isUser || msg.is_user || false,
            timestamp: msg.timestamp || msg.created_at || new Date().toISOString(),
            isLoading: false,
            isStreaming: false,
            isHtml: msg.isHtml || msg.is_html || false,
            sender: msg.sender || (msg.isUser ? 'user' : 'bot'),
            senderName: msg.senderName || null,
            attachment: msg.attachment || null
          }));
          // Agent messages already in the history are not fetched again by polling
          state.handoff.lastId = Math.max(0, ...historyMessages.map(m => (typeof m.id === 'number' ? m.id : 0)));
        } else {
          // No history, show welcome message
          state.messages = [{
            id: 1,
            text: WELCOME_TEXT,
            isWelcome: true, // drawn as the WelcomeHero, not as a chat bubble
            isUser: false,
            timestamp: new Date().toISOString(),
            isLoading: false,
            isStreaming: false,
            isHtml: false
          }];
        }
      })
      .addCase(fetchChatHistory.rejected, (state) => {
        state.isLoadingHistory = false; // Set history loading to false
        // Show welcome message if history fetch fails
        state.messages = [{
          id: 1,
          text: WELCOME_TEXT,
          isWelcome: true, // drawn as the WelcomeHero, not as a chat bubble
          isUser: false,
          timestamp: new Date().toISOString(),
          isLoading: false,
          isStreaming: false,
          isHtml: false
        }];
      })
      .addCase(fetchAgentUpdates.fulfilled, (state) => {
        // State and messages are applied by handoffStateUpdated / agentMessageRevealed
        state.handoff.checked = true;  // a failed check must not block the widget for good
      })
      // Streaming bot response
      .addCase(streamBotResponse.pending, (state) => {
        state.loading = true;
        state.isStreaming = true;
      })
      .addCase(streamBotResponse.fulfilled, (state) => {
        state.loading = false;
        state.isStreaming = false;
      })
      .addCase(streamBotResponse.rejected, (state) => {
        state.loading = false;
        state.isStreaming = false;
      })
      // Session closing
      .addCase(closeSession.pending, (state) => {
        state.isClosingSession = true;
      })
      .addCase(closeSession.fulfilled, (state, action) => {
        state.isClosingSession = false;
        const { shouldCreateNew, success, newSessionId } = action.payload;
        
        if (success) {
          if (shouldCreateNew && newSessionId) {
            // New session was created successfully
            // The initializeSession action will handle setting the new sessionId and messages
            // console.log('Session closed and new session created');
          } else {
            // Just closing, clear everything
            state.sessionId = null;
            state.messages = [];
          }
        }
      })
      .addCase(closeSession.rejected, (state, action) => {
        state.isClosingSession = false;
        console.error('Failed to close session:', action.error.message);
        // Could show an error message to user here
      })
      // Add error handling for all async thunks
      .addMatcher(
        (action) => action.type.endsWith('/rejected'),
        (state, action) => {
          state.loading = false;
          state.isStreaming = false;
          state.networkError = action.error.message;
        }
      );
  }
});

export const { 
  addMessage, 
  removeMessage,
  setChatLocked,
  handoffStateUpdated,
  agentMessageRevealed,
  setAgentTyping,
  markSentToAgent,
  attachmentSending,
  attachmentSent,
  attachmentFailed,
  setInputText,
  clearInput, 
  setIsTyping,
  setIsStreaming,
  sendUserMessage,
  updateStreamingMessage,
  completeStreamingMessage,
  setMessageSuggestions,
  setMessageContactPicker,
  setClientData,
  clearClientData,
  setChatHistory,
  clearSession,
  resetToWelcomeMessage,
  // Add this new action
  clearMessagesExceptCurrent,
  setNetworkError,
  clearNetworkError,
  setSessionError,
  clearSessionError,
  incrementRetryCount,
  setOfflineStatus,
  // Queue management actions
  enqueueMessage,
  dequeueMessage,
  clearMessageQueue,
  setProcessingQueue
} = chatSlice.actions;

// Selectors
export const selectMessages = (state) => state.chat.messages;
export const selectInputText = (state) => state.chat.inputText;
export const selectIsTyping = (state) => state.chat.isTyping;
export const selectIsStreaming = (state) => state.chat.isStreaming;
export const selectLoading = (state) => state.chat.loading;
export const selectSessionId = (state) => state.chat.sessionId;
export const selectIsInitializingSession = (state) => state.chat.isInitializingSession;
export const selectClientData = (state) => state.chat.clientData;
export const selectIsLoadingHistory = (state) => state.chat.isLoadingHistory;
export const selectIsClosingSession = (state) => state.chat.isClosingSession;
export const selectNetworkError = (state) => state.chat.networkError;
export const selectRetryCount = (state) => state.chat.retryCount;
export const selectIsOffline = (state) => state.chat.isOffline;
export const selectSessionError = (state) => state.chat.sessionError;
// Queue selectors
export const selectMessageQueue = (state) => state.chat.messageQueue;
export const selectIsProcessingQueue = (state) => state.chat.isProcessingQueue;
export const selectHandoff = (state) => state.chat.handoff;

export default chatSlice.reducer; 